// src/admin/AdminInactiveUsers.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  TablePagination,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

const STAGE_CARDS = [
  { key: 'Inactive', label: 'Inactive Users', description: '90+ days with no TokenLedger activity', color: '#2563eb' },
  { key: 'ApproachingEligible', label: 'Approaching (7d)', description: 'Within 1 week of inactive threshold', color: '#f59e0b' },
  { key: 'Eligible', label: 'Eligible', description: '3+ months inactive, not yet in pipeline', color: '#64748b' },
  { key: 'AwaitingNotice1', label: 'Awaiting Notice 1', description: 'Enrolled, first email pending', color: '#0ea5e9' },
  { key: 'Notice1', label: 'Notice 1 Sent', description: 'Waiting for notice 2 (1 week)', color: '#6366f1' },
  { key: 'Notice2', label: 'Notice 2 Sent', description: 'Waiting for final notice (1 week)', color: '#8b5cf6' },
  { key: 'FinalNotice', label: 'Final Notice Sent', description: 'Deletion scheduled', color: '#d97706' },
  { key: 'PendingDelete', label: 'Pending Delete', description: 'Due for hard delete', color: '#dc2626' },
  { key: 'Cancelled', label: 'Cancelled (30d)', description: 'Re-activated after notice', color: '#16a34a' },
  { key: 'Deleted', label: 'Deleted (30d)', description: 'Hard-deleted by job', color: '#991b1b' },
];

const COUNT_FIELD = {
  Inactive: 'InactiveUsersCount',
  ApproachingEligible: 'ApproachingEligibleCount',
  Eligible: 'EligibleCount',
  AwaitingNotice1: 'AwaitingNotice1Count',
  Notice1: 'Notice1Count',
  Notice2: 'Notice2Count',
  FinalNotice: 'FinalNoticeCount',
  PendingDelete: 'PendingDeleteCount',
  Cancelled: 'CancelledRecentCount',
  Deleted: 'DeletedRecentCount',
};

const searchFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(10px)',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255,255,255,0.08)'
      : '1px solid rgba(255,255,255,0.2)',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(102, 126, 234, 0.5)',
    },
    '&.Mui-focused': {
      borderColor: '#4FACFE',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
  },
});

const gradientButtonSx = {
  background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
  },
  transition: 'all 0.3s ease',
  px: 3,
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function AdminInactiveUsers() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [selectedStage, setSelectedStage] = useState('Inactive');
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const response = await api.get(API.adminInactiveUserDeletionSummary);
      setSummary(response.data.summary ?? {});
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to load summary');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadUsers = useCallback(async (stage, search, pageIndex, pageSize) => {
    if (!search?.trim()) return;

    setLoadingUsers(true);
    try {
      const response = await api.get(API.adminInactiveUserDeletionUsers(stage), {
        params: {
          searchTerm: search.trim(),
          page: pageIndex + 1,
          pageSize,
        },
      });
      setUsers(response.data.users ?? []);
      setTotalUsers(Number(response.data.total ?? response.data.count) || 0);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to load users');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!currentSearch) return;
    loadUsers(selectedStage, currentSearch, page, rowsPerPage);
  }, [selectedStage, currentSearch, page, rowsPerPage, loadUsers]);

  const handleRefresh = () => {
    loadSummary();
    if (currentSearch) {
      loadUsers(selectedStage, currentSearch, page, rowsPerPage);
    }
  };

  const handleSearch = () => {
    const term = searchTerm.trim();
    if (!term) {
      setErrorMsg('Enter username, name, email, or mobile number to search');
      setUsers([]);
      setTotalUsers(0);
      setCurrentSearch('');
      setHasSearched(false);
      setPage(0);
      return;
    }
    setPage(0);
    setCurrentSearch(term);
    setHasSearched(true);
  };

  const handleStageChange = (stageKey) => {
    setSelectedStage(stageKey);
    setPage(0);
    if (!currentSearch) {
      setUsers([]);
      setTotalUsers(0);
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const inactiveDays = summary?.InactiveDays ?? 90;
  const showApproachingColumn = selectedStage === 'ApproachingEligible';
  const showNoticeColumns = !showApproachingColumn
    && !['Inactive', 'Eligible', 'Cancelled', 'Deleted'].includes(selectedStage);

  const selectedCard = STAGE_CARDS.find((c) => c.key === selectedStage);

  const tableColumns = useMemo(() => {
    const cols = [
      {
        header: 'User ID',
        accessor: 'UserId',
        render: (value) => (
          <Typography variant="body2" fontWeight={500}>{value ?? '—'}</Typography>
        ),
        style: { textAlign: 'center' },
      },
      {
        header: 'Username',
        accessor: 'UserName',
        render: (value, row) => (
          row.UserId ? (
            <Box
              component={Link}
              to={`/admin/user-details/${row.UserId}`}
              sx={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography fontWeight={500}>{value || '—'}</Typography>
            </Box>
          ) : (
            <Typography fontWeight={500}>{value || '—'}</Typography>
          )
        ),
      },
      {
        header: 'Name',
        accessor: 'FirstName',
        render: (value, row) => (
          <Box>
            <Typography>{value || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">{row.LastName || '—'}</Typography>
          </Box>
        ),
      },
      {
        header: 'Email',
        accessor: 'Email',
        render: (email) => (
          <Typography variant="body2">{email || '—'}</Typography>
        ),
      },
      {
        header: 'Mobile',
        accessor: 'MobileNumber',
        render: (value) => (
          <Typography variant="body2">{value || '—'}</Typography>
        ),
      },
      {
        header: 'Last Activity',
        accessor: 'LastActivityAt',
        render: (value) => (
          value ? (
            <Typography variant="body2">{formatDate(value)}</Typography>
          ) : (
            <Chip label="Never" size="small" variant="outlined" color="default" />
          )
        ),
      },
      {
        header: 'Days Inactive',
        accessor: 'DaysInactive',
        render: (days) => (
          <Chip
            label={days ?? '—'}
            size="small"
            color={days >= 90 ? 'error' : days >= 83 ? 'warning' : 'default'}
            variant="outlined"
          />
        ),
        style: { textAlign: 'center' },
      },
    ];

    if (showApproachingColumn) {
      cols.push({
        header: 'Days Until Eligible',
        accessor: 'DaysUntilEligible',
        render: (days) => (
          <Chip label={days ?? '—'} size="small" color="warning" variant="outlined" />
        ),
        style: { textAlign: 'center' },
      });
    }

    if (showNoticeColumns) {
      cols.push(
        {
          header: 'Notice 1',
          accessor: 'Notice1SentAt',
          render: (value) => <Typography variant="body2">{formatDate(value)}</Typography>,
        },
        {
          header: 'Notice 2',
          accessor: 'Notice2SentAt',
          render: (value) => <Typography variant="body2">{formatDate(value)}</Typography>,
        },
        {
          header: 'Final Notice',
          accessor: 'FinalNoticeSentAt',
          render: (value) => <Typography variant="body2">{formatDate(value)}</Typography>,
        },
        {
          header: 'Scheduled Delete',
          accessor: 'ScheduledDeleteAt',
          render: (value) => (
            value ? (
              <Chip label={formatDate(value)} size="small" color="error" variant="outlined" />
            ) : (
              <Typography variant="body2">—</Typography>
            )
          ),
        },
      );
    }

    return cols;
  }, [showApproachingColumn, showNoticeColumns]);

  return (
    <AdminPageWrapper
      title="Inactive Users"
      subtitle={`Users with no TokenLedger activity for ${inactiveDays}+ days. Search by username, name, email, or mobile to find users in the selected stage.`}
    >
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAGE_CARDS.map((card) => {
          const count = summary?.[COUNT_FIELD[card.key]] ?? 0;
          const selected = selectedStage === card.key;
          return (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Card
                variant="outlined"
                sx={(theme) => ({
                  height: '100%',
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderColor: selected ? card.color : theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(255,255,255,0.2)',
                  borderWidth: selected ? 2 : 1,
                  boxShadow: selected ? `0 4px 20px ${card.color}33` : 'none',
                  transition: 'all 0.25s ease',
                })}
              >
                <CardActionArea onClick={() => handleStageChange(card.key)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h4" sx={{ color: card.color, fontWeight: 700 }}>
                      {loadingSummary ? '…' : count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          label="Search by username, name, email, or mobile"
          placeholder="Type at least part of a username, name, email, or mobile number"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!searchTerm.trim() || loadingUsers}
          sx={gradientButtonSx}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={loadingSummary || loadingUsers}
          startIcon={loadingSummary || loadingUsers ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
        >
          Refresh
        </Button>
      </Stack>

      {selectedCard && hasSearched && currentSearch && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Results for <strong>&quot;{currentSearch}&quot;</strong> in <strong>{selectedCard.label}</strong> ({totalUsers} found)
        </Typography>
      )}

      <ProfessionalTable
        columns={tableColumns}
        data={users.map((user) => ({ ...user, id: user.UserId ?? user.QueueId }))}
        onRowClick={(row) => {
          if (row.UserId) navigate(`/admin/user-details/${row.UserId}`);
        }}
        loading={loadingUsers}
        emptyMessage={
          loadingUsers
            ? ''
            : hasSearched && currentSearch
              ? `No users found in ${selectedCard?.label ?? 'this stage'} for "${currentSearch}"`
              : 'Enter a search term and click Search to find users'
        }
      />

      {hasSearched && currentSearch && (
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{
            color: 'text.primary',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: 'text.primary',
            },
          }}
        />
      )}

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg('')} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

export default AdminInactiveUsers;
